/**
 * ============================================================================
 * RegisterPage.js - User Registration Page Component
 * ============================================================================
 * Handles new user account creation with subscription plan selection.
 *
 * Created: November 27, 2025 - Phase 2C Multi-Tenancy
 * Updated: January 23, 2026 - Phase 3D Subscription Billing
 * Updated: January 26, 2026 - Added professional success modal
 * Updated: January 27, 2026 - All signups require CC with 30-day trial
 * Updated: January 30, 2026 - Added Founders invite link support (?plan=founders)
 * Updated: April 22, 2026 - UTM attribution capture from landing pages
 * Updated: May 27, 2026 - Step 3 Free + Starter Tier Rollout (this file):
 *   - Added Free tier as default and 5-tier picker (Free / Starter $4.99 /
 *     Plus $9.99 / Professional $14.99 / League $24.99)
 *   - Reads `?tier=X` URL param from landing page to preselect tier
 *   - Free path: POST /api/auth/register/free, skip Stripe, navigate to /signup/success
 *   - Paid path: existing flow preserved (POST /auth/register → Stripe checkout)
 *   - Removed all "30-day free trial" / "credit card required" copy
 *   - New GA4 event `free_signup` for the Free signup path
 *   - Founders invite flow preserved unchanged
 *
 * TIER ID NOTE (canonical keys — Phase 4 rename live as of June 1 2026):
 *   free         = $0 Free tier
 *   starter      = $4.99 Starter
 *   plus         = $9.99 Plus
 *   professional = $14.99 Professional
 *   league       = $24.99 League
 *   founders     = $49.99/yr Founders (admin-assigned)
 *
 * SUBSCRIPTION FLOW:
 * - Free tier: account created via /api/auth/register/free, immediately active,
 *              navigates to /signup/success (welcome page).
 * - Paid tiers: account created via /api/auth/register, then redirected to
 *               Stripe Checkout for payment.
 * - Founders: paid flow (annual only), preserved exactly as before.
 *
 * FOUNDERS INVITE:
 * - URL: /register?plan=founders
 * - Shows special Founders-only UI ($49.99/year, unlimited players)
 * - Rate locked for life
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOutsideUSNotice } from './OutsideUSNotice';   // Oct 10, 2026

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// ============================================================================
// Tier configuration
// ----------------------------------------------------------------------------
// `id` is the canonical tier key the backend expects (post-Phase-4 rename,
// June 1 2026): free, starter, plus, professional, league. These literal
// strings must match subscriptionService.js TIER_CONFIG / getStripePriceId
// keys, adminRoutes validTiers, and the stripeRoutes.js webhook resolution map.
// The old transitional keys (starter_new, basic, enterprise) no longer exist.
// ============================================================================
const SUBSCRIPTION_TIERS = [
  {
    id: 'free',
    name: 'Free',
    players: 'Up to 8',
    monthlyPrice: 0,
    annualPrice: 0,
    perPlayerAnnual: null,
    popular: false,
    isFree: true,
    tagline: 'No credit card required',
  },
  {
    id: 'starter', // canonical key (Phase 4 rename, June 1 2026: was 'starter_new')
    name: 'Starter',
    players: 'Up to 20',
    monthlyPrice: 4.99,
    annualPrice: 47.90, // Stripe's live Starter annual price (the $49.90 price is archived)
    perPlayerAnnual: 2.40,
    popular: true,
  },
  {
    id: 'plus', // canonical key (Phase 4 rename, June 1 2026: was 'starter')
    name: 'Plus',
    players: 'Up to 40',
    monthlyPrice: 9.99,
    annualPrice: 95.90,
    perPlayerAnnual: 2.40,
    popular: false,
    games: true,   // Oct 5, 2026: games come with Plus and above
  },
  {
    id: 'professional', // canonical key (Phase 4 rename, June 1 2026: was 'basic')
    name: 'Professional',
    players: 'Up to 100',
    monthlyPrice: 14.99,
    annualPrice: 143.90,
    perPlayerAnnual: 1.44,
    popular: false,
    games: true,
  },
  {
    id: 'league',
    name: 'League',
    players: 'Up to 250',
    monthlyPrice: 24.99,
    annualPrice: 239.90,
    perPlayerAnnual: 0.96,
    popular: false,
    games: true,
  },
];

// Allowed `?tier=X` URL param values. Anything else falls back to 'free'.
const VALID_URL_TIERS = new Set(SUBSCRIPTION_TIERS.map(t => t.id));

// ============================================================================
// GA4 tracking helper - safe to call even if gtag isn't loaded
// ============================================================================
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

// ============================================================================
// Main RegisterPage Component
// ============================================================================
function RegisterPage() {
  const navigate = useNavigate();
  const outsideNotice = useOutsideUSNotice(API_URL);   // Oct 10, 2026: outside-the-US sign-up notice
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuth();

  // ==========================================================================
  // RESUME-CHECKOUT MODE (B1)
  // --------------------------------------------------------------------------
  // A user with a `setup_pending` org (registered but never completed Stripe
  // checkout) is sent here by LoginPage with { resumeCheckout: true } in route
  // state. They already have an account + org, so they must NOT go through the
  // account-creation form again (that would hit the duplicate-email block).
  // Instead we show ONLY the plan selector and, on confirm, create a checkout
  // session for the EXISTING org. We read the pending org id from route state,
  // falling back to sessionStorage so a hard refresh on this page doesn't lose
  // it. (B1 = user re-picks their plan; we do not pre-fill the original tier.)
  // ==========================================================================
  const isResumeCheckout = !!(location.state?.resumeCheckout);
  const resumeOrgId = location.state?.organizationId
    || sessionStorage.getItem('pendingOrgId')
    || '';
  const resumeOrgName = location.state?.organizationName || '';
  
  // Check for Founders invite link
  const isFoundersInvite = searchParams.get('plan')?.toLowerCase() === 'founders';
  
  // Read ?tier= URL param from landing page. Defaults to 'free' if absent
  // or unrecognized. Founders invite always wins if both are present.
  const urlTier = searchParams.get('tier');
  // "I'm running one event" (Sept 28, 2026 — Event Pass): /register?event=1 from the
  // /events page. The account is Free; the Event Pass window opens on the dashboard
  // after signup (App.js EventPassModalHost reads the tto_open_event_pass flag).
  // Deliberately NOT ?plan= — that parameter routes through /welcome (referrals).
  const isEventDoor = searchParams.get('event') === '1';
  // Sept 30, 2026: the Event Pass has its own door (/events/start: pass, event name,
  // payment — no plan picker). Old ?event=1 links go there.
  useEffect(() => {
    if (isEventDoor) navigate('/events/start', { replace: true });
  }, [isEventDoor]);   // eslint-disable-line react-hooks/exhaustive-deps
  const initialTier = isFoundersInvite
    ? 'founders'
    : isEventDoor ? 'free'
    : (urlTier && VALID_URL_TIERS.has(urlTier) ? urlTier : 'free');
  
  // Check if user came via referral link (ref= or plan= parameter)
  const hasReferralParams = searchParams.get('ref') || searchParams.get('plan');
  
  // Form state - Step 1: Account Info
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  
  // Form state - Step 2: Plan Selection
  const [selectedTier, setSelectedTier] = useState(initialTier);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  
  // Convenience flag derived from selectedTier
  const isFreeTier = selectedTier === 'free';
  
  // Referral code
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referralValidation, setReferralValidation] = useState({ valid: null, message: '', referrerName: '' });
  const [validatingReferral, setValidatingReferral] = useState(false);
  
  // UI state
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Redirect if already authenticated - BUT NOT if they came via referral link
  // This allows users to: try demo → click referral link → create NEW account.
  // ALSO not if resuming checkout for an existing setup_pending org: those users
  // ARE authenticated, and bouncing them to /dashboard would trap them (the
  // dashboard is blocked until checkout completes).
  useEffect(() => {
    if (!isLoading && isAuthenticated && !hasReferralParams && !isResumeCheckout) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, hasReferralParams, isResumeCheckout]);

  // In resume mode, skip the account-info step entirely and show the plan
  // selector (Step 2) immediately.
  useEffect(() => {
    if (isResumeCheckout) {
      setCurrentStep(2);
    }
  }, [isResumeCheckout]);
  
  // Redirect referral links to Welcome page (unless they came FROM welcome page)
  // This ensures users clicking old /register?ref= links see the nice landing page first
  useEffect(() => {
    const refCode = searchParams.get('ref');
    const fromWelcome = searchParams.get('from') === 'welcome';
    const plan = searchParams.get('plan');
    
    // If has ref param but NOT coming from welcome page, redirect to welcome
    if (refCode && !fromWelcome) {
      const params = new URLSearchParams();
      params.set('ref', refCode);
      if (plan) params.set('plan', plan);
      navigate(`/welcome?${params.toString()}`, { replace: true });
      return;
    }
  }, [searchParams, navigate]);
  
  // Track referral link clicks (when someone arrives via ?ref= parameter)
  // Note: This is now handled by WelcomePage, but keeping for direct /register access
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      // Fire-and-forget - we don't need to wait for the response
      fetch(`${API_URL}/referral/track-click?code=${encodeURIComponent(refCode.toUpperCase())}`)
        .then(response => response.json())
        .then(data => {
          console.log('📊 Referral click tracked:', refCode);
        })
        .catch(err => {
          // Silent fail - tracking shouldn't break the registration flow
          console.log('Referral tracking failed (non-critical):', err.message);
        });
    }
  }, []); // Empty deps - only run once on mount
  
  // Validate referral code when it changes (with debounce)
  useEffect(() => {
    if (!referralCode || referralCode.length < 4) {
      setReferralValidation({ valid: null, message: '', referrerName: '' });
      return;
    }
    
    const validateTimer = setTimeout(async () => {
      setValidatingReferral(true);
      try {
        const response = await fetch(`${API_URL}/subscription/validate-referral`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ referralCode: referralCode.toUpperCase() })
        });
        
        const data = await response.json();
        
        if (response.ok && data.valid) {
          setReferralValidation({
            valid: true,
            message: `✓ Valid! Referred by ${data.referrerName}`,
            referrerName: data.referrerName
          });
        } else {
          setReferralValidation({
            valid: false,
            message: data.error || 'Invalid referral code',
            referrerName: ''
          });
        }
      } catch (err) {
        setReferralValidation({
          valid: false,
          message: 'Could not validate code',
          referrerName: ''
        });
      } finally {
        setValidatingReferral(false);
      }
    }, 500); // 500ms debounce
    
    return () => clearTimeout(validateTimer);
  }, [referralCode]);
  
  // Validate Step 1
  const validateStep1 = () => {
    const errors = {};
    
    if (!name.trim()) {
      errors.name = 'Name is required';
    } else if (name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        errors.email = 'Please enter a valid email address';
      }
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (!organizationName.trim()) {
      errors.organizationName = 'Organization name is required';
    }
    
    if (!agreeToTerms) {
      errors.terms = 'You must agree to the terms';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle Step 1 -> Step 2
  const handleContinueToPlans = (e) => {
    e.preventDefault();
    setError('');
    
    if (validateStep1()) {
      setCurrentStep(2);
      window.scrollTo(0, 0);
    }
  };
  
  // ==========================================================================
  // Submit handler
  // --------------------------------------------------------------------------
  // Branches on selectedTier:
  //   - 'free'      → POST /api/auth/register/free, navigate to /signup/success
  //   - 'founders'  → existing paid flow with annual billing forced
  //   - everything else → existing paid flow via Stripe Checkout
  // ==========================================================================

  // ==========================================================================
  // RESUME-CHECKOUT submit handler (B1)
  // --------------------------------------------------------------------------
  // Used only in resume mode. The org and user already exist; we do NOT call
  // /auth/register. We create a Stripe Checkout session for the EXISTING org
  // using the pending token, then redirect to Stripe. Mirrors the checkout
  // block in handleSubmit (the paid path), minus registration.
  // ==========================================================================
  const handleResumeCheckout = async (e) => {
    e.preventDefault();
    setError('');

    // Free has no checkout step. If a resuming user picks Free, send them to the
    // dashboard — but their org is still setup_pending, so we can't silently
    // grant access. Tell them Free selection needs admin help rather than fail
    // confusingly. (Resume is intended for completing the original paid intent.)
    if (isFreeTier) {
      setError('To switch this organization to the Free plan, please contact support — Free selection is not yet self-serve from this screen.');
      return;
    }

    if (!resumeOrgId) {
      setError('We could not identify your organization to resume. Please sign in again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const tempToken = sessionStorage.getItem('pendingAccessToken');
      if (!tempToken) {
        throw new Error('Your session expired. Please sign in again to resume checkout.');
      }

      const checkoutResponse = await fetch(`${API_URL}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({
          tier: selectedTier,
          billingCycle: selectedTier === 'founders' ? 'annual' : billingCycle,
          successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/checkout/cancel`
        })
      });

      const checkoutData = await checkoutResponse.json();

      if (!checkoutResponse.ok) {
        throw new Error(checkoutData.error || 'Could not create checkout session');
      }

      if (checkoutData.url) {
        sessionStorage.setItem('checkoutOrgId', resumeOrgId);
        trackEvent('checkout_resumed', {
          tier: selectedTier,
          billing_cycle: selectedTier === 'founders' ? 'annual' : billingCycle
        });
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Resume checkout error:', err);
      setError(err.message || 'Could not resume checkout. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Oct 10, 2026: a visitor who appears to be outside the US and Canada sees the
  // notice first; Continue creates the account, Cancel leaves the form as it was.
  const handleSubmit = (e) => {
    e.preventDefault();
    outsideNotice.guard(() => submitRegistration());
  };

  const submitRegistration = async () => {
    setError('');
    setIsSubmitting(true);
    
    try {
      // Read marketing attribution from sessionStorage (captured on landing page).
      // May be null/empty for direct traffic or if user opened /register directly.
      let attribution = {};
      try {
        const stored = sessionStorage.getItem('tto_attribution');
        if (stored) attribution = JSON.parse(stored);
      } catch (e) {
        // Malformed JSON or storage blocked - continue without attribution.
        console.warn('Could not read tto_attribution from sessionStorage:', e);
      }
      
      // Common request body for both Free and paid paths.
      const baseRegistrationBody = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        organizationName: organizationName.trim(),
        referralCode: referralCode.trim().toUpperCase() || undefined,
        // Marketing attribution (all optional, nullable in DB)
        utmSource:    attribution.utm_source   || null,
        utmMedium:    attribution.utm_medium   || null,
        utmCampaign:  attribution.utm_campaign || null,
        utmContent:   attribution.utm_content  || null,
        landingPage:  attribution.landing_page || null,
        outsideNoticeAccepted: outsideNotice.accepted(),
      };
      
      // ========================================================================
      // FREE TIER PATH — no Stripe, immediate account activation
      // ========================================================================
      if (isFreeTier) {
        const freeResponse = await fetch(`${API_URL}/auth/register/free`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...baseRegistrationBody,
            subscriptionTier: 'free',
          })
        });
        
        const freeData = await freeResponse.json();
        
        if (!freeResponse.ok) {
          throw new Error(freeData.error || 'Registration failed');
        }
        
        // Free signup is immediately active — no Stripe gate. Store tokens
        // in localStorage directly so the user is logged in when they reach
        // /signup/success. Response shape assumed to match /auth/register
        // (accessToken + refreshToken + organization). If the endpoint
        // returns a different shape, the /signup/success page will surface
        // it as "we couldn't log you in automatically" rather than blow up.
        if (freeData.accessToken) {
          localStorage.setItem('accessToken', freeData.accessToken);
        }
        if (freeData.refreshToken) {
          localStorage.setItem('refreshToken', freeData.refreshToken);
        }
        if (freeData.organization?.id) {
          localStorage.setItem('organizationId', freeData.organization.id);
        }
        
        // GA4: Track free signup. This is the new event added in Step 3.
        // The /signup/success page fires `registration_completed` separately
        // (analogous to how paid path fires it before Stripe redirect).
        trackEvent('free_signup', {
          has_referral: !!referralCode.trim(),
        });
        
        if (isEventDoor) { try { localStorage.setItem('tto_open_event_pass', '1'); } catch (e) { /* storage blocked */ } }
        console.log('📊 Free signup complete, navigating to /signup/success');
        navigate('/signup/success', { replace: true });
        return;
      }
      
      // ========================================================================
      // PAID TIER PATH — existing flow, preserved
      // ========================================================================
      const registerResponse = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...baseRegistrationBody,
          subscriptionTier: selectedTier,
          billingCycle: selectedTier === 'founders' ? 'annual' : billingCycle,
        })
      });
      
      const registerData = await registerResponse.json();
      
      if (!registerResponse.ok) {
        throw new Error(registerData.error || 'Registration failed');
      }
      
      // IMPORTANT: Do NOT store tokens in localStorage yet!
      // The org is in 'setup_pending' status - dashboard access is blocked.
      // Store temporarily in sessionStorage for Stripe checkout redirect only.
      // CheckoutSuccessPage will move them to localStorage after payment confirms.
      if (registerData.accessToken) {
        sessionStorage.setItem('pendingAccessToken', registerData.accessToken);
        sessionStorage.setItem('pendingRefreshToken', registerData.refreshToken);
        sessionStorage.setItem('pendingOrgId', registerData.organization?.id || '');
      }
      
      // GA4: Track successful registration (account created, before Stripe)
      trackEvent('registration_completed', {
        tier: selectedTier,
        billing_cycle: selectedTier === 'founders' ? 'annual' : billingCycle,
        has_referral: !!referralCode.trim()
      });
      
      // Log funnel step: registration complete, heading to Stripe
      console.log('📊 Registration complete, redirecting to Stripe checkout...');
      
      // Create Stripe Checkout session using the temporary token
      const tempToken = registerData.accessToken || sessionStorage.getItem('pendingAccessToken');
      const checkoutResponse = await fetch(`${API_URL}/subscription/checkout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({
          tier: selectedTier,
          billingCycle: selectedTier === 'founders' ? 'annual' : billingCycle,
          successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/checkout/cancel`
        })
      });
      
      const checkoutData = await checkoutResponse.json();
      
      if (!checkoutResponse.ok) {
        throw new Error(checkoutData.error || 'Could not create checkout session');
      }
      
      // Redirect to Stripe Checkout
      if (checkoutData.url) {
        // Store org ID for abandonment tracking if user cancels at Stripe
        sessionStorage.setItem('checkoutOrgId', registerData.organization?.id || '');
        
        // GA4: Track checkout started (user is about to be redirected to Stripe)
        trackEvent('checkout_started', {
          tier: selectedTier,
          billing_cycle: selectedTier === 'founders' ? 'annual' : billingCycle
        });
        
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL returned');
      }
      
    } catch (err) {
      console.error('Registration error:', err);
      
      // If registration succeeded but Stripe failed, log the abandonment.
      // Only applies to paid path (Free has no Stripe step).
      if (!isFreeTier) {
        const pendingOrgId = sessionStorage.getItem('pendingOrgId');
        if (pendingOrgId && err.message?.includes('checkout')) {
          try {
            await fetch(`${API_URL}/stripe/log-abandonment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ organizationId: pendingOrgId, reason: 'checkout_creation_failed' })
            });
          } catch (logErr) {
            // Silent - tracking shouldn't break flow
          }
        }
      }
      
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  // Clear field error when user types
  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setError('');
  };
  
  // Get selected tier details - handle founders specially
  const selectedTierData = selectedTier === 'founders'
    ? { id: 'founders', name: 'Founders', players: 'Unlimited', annualPrice: 49.99, monthlyPrice: null }
    : SUBSCRIPTION_TIERS.find(t => t.id === selectedTier);
  const displayPrice = selectedTier === 'founders'
    ? 49.99
    : (billingCycle === 'annual' ? selectedTierData?.annualPrice : selectedTierData?.monthlyPrice);
  
  // Show loading while checking auth
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)'
      }}>
        <div style={{ color: '#fff', fontSize: 18 }}>Loading...</div>
      </div>
    );
  }
  
  // --------------------------------------------------------------------------
  // Heading / subhead — tier-aware copy with all 30-day-trial language removed.
  // --------------------------------------------------------------------------
  const step1Heading = isFoundersInvite
    ? 'Founders Invitation'
    : (isFreeTier ? 'Create Your Free Account' : 'Create Your Account');
  
  const step1Subhead = isFoundersInvite
    ? 'Exclusive early adopter pricing'
    : (isFreeTier
        ? 'Free for up to 8 players · No credit card required'
        : 'It only takes a minute');
  
  const step2Heading = isFoundersInvite ? 'Founders Invitation' : 'Confirm Your Plan';
  const step2Subhead = isFoundersInvite
    ? 'Exclusive early adopter pricing'
    : 'Change at any time';
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
      padding: 24
    }}>
      {outsideNotice.modal}
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: currentStep === 1 ? 28 : 24,
        width: '100%',
        maxWidth: 480,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        transition: 'max-width 0.3s ease'
      }}>
        {/* Logo and Title */}
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{
            width: 44,
            height: 44,
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 8px',
            fontSize: 22
          }}>
            ⛳
          </div>
          <h1 style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 4px 0'
          }}>
            {currentStep === 1 ? step1Heading : step2Heading}
          </h1>
          <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>
            {currentStep === 1 ? step1Subhead : step2Subhead}
          </p>
        </div>
        
        {/* Step Indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 8,
          marginBottom: 14
        }}>
          <div style={{
            width: 32,
            height: 4,
            background: '#2d6a4f',
            borderRadius: 2
          }} />
          <div style={{
            width: 32,
            height: 4,
            background: currentStep >= 2 ? '#2d6a4f' : '#e5e7eb',
            borderRadius: 2,
            transition: 'background 0.3s'
          }} />
        </div>
        
        {isEventDoor && (
          <div data-testid="event-door-banner" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px', margin: '0 0 14px', fontSize: 13, color: '#14532d', lineHeight: 1.5 }}>
            🎟️ <strong>Running one event?</strong> Create your free account first, then buy an Event Pass for your event — one-time, no subscription.
          </div>
        )}

        {/* Step 1: Account Info */}
        {currentStep === 1 && (
          <form onSubmit={handleContinueToPlans}>
            {/* Organization Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ 
                display: 'block', 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#374151',
                marginBottom: 6
              }}>
                Organization Name *
              </label>
              <input
                type="text"
                value={organizationName}
                onChange={(e) => { setOrganizationName(e.target.value); clearFieldError('organizationName'); }}
                placeholder="e.g., Sunset Golf Club"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: fieldErrors.organizationName ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2d6a4f'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.organizationName ? '#ef4444' : '#e5e7eb'}
              />
              {fieldErrors.organizationName && (
                <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{fieldErrors.organizationName}</p>
              )}
            </div>
            
            {/* Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ 
                display: 'block', 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#374151',
                marginBottom: 6
              }}>
                Your Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); clearFieldError('name'); }}
                placeholder="John Smith"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: fieldErrors.name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2d6a4f'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.name ? '#ef4444' : '#e5e7eb'}
              />
              {fieldErrors.name && (
                <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{fieldErrors.name}</p>
              )}
            </div>
            
            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ 
                display: 'block', 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#374151',
                marginBottom: 6
              }}>
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
                placeholder="john@golfclub.com"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: fieldErrors.email ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2d6a4f'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.email ? '#ef4444' : '#e5e7eb'}
              />
              {fieldErrors.email && (
                <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{fieldErrors.email}</p>
              )}
            </div>
            
            {/* Password */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ 
                display: 'block', 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#374151',
                marginBottom: 6
              }}>
                Password *
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
                placeholder="At least 6 characters"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: fieldErrors.password ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2d6a4f'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.password ? '#ef4444' : '#e5e7eb'}
              />
              {fieldErrors.password && (
                <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{fieldErrors.password}</p>
              )}
            </div>
            
            {/* Confirm Password */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ 
                display: 'block', 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#374151',
                marginBottom: 6
              }}>
                Confirm Password *
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError('confirmPassword'); }}
                placeholder="Re-enter password"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: fieldErrors.confirmPassword ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2d6a4f'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.confirmPassword ? '#ef4444' : '#e5e7eb'}
              />
              {fieldErrors.confirmPassword && (
                <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{fieldErrors.confirmPassword}</p>
              )}
            </div>
            
            {/* Terms Checkbox */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                gap: 8,
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => { setAgreeToTerms(e.target.checked); clearFieldError('terms'); }}
                  style={{ marginTop: 2 }}
                />
                <span style={{ fontSize: 14, color: fieldErrors.terms ? '#ef4444' : '#374151' }}>
                  I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#2d6a4f' }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#2d6a4f' }}>Privacy Policy</a>
                </span>
              </label>
              {fieldErrors.terms && (
                <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{fieldErrors.terms}</p>
              )}
            </div>
            
            {/* Error Message */}
            {error && (
              <div style={{
                padding: '12px 16px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                color: '#b91c1c',
                fontSize: 14,
                marginBottom: 16
              }}>
                {error}
              </div>
            )}
            
            {/* Continue Button */}
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(45, 106, 79, 0.3)'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.9'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Continue to Plan Selection →
            </button>
          </form>
        )}
        
        {/* Step 2: Plan Selection */}
        {currentStep === 2 && (
          <form onSubmit={isResumeCheckout ? handleResumeCheckout : handleSubmit}>
            {/* Back Button — hidden in resume mode (no account-info step to return to) */}
            {!isResumeCheckout && (
            <button
              type="button"
              onClick={() => { setCurrentStep(1); window.scrollTo(0, 0); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#2d6a4f',
                fontSize: 14,
                cursor: 'pointer',
                marginBottom: 16,
                padding: 0,
                fontWeight: 600
              }}
            >
              ← Back to account info
            </button>
            )}

            {/* Resume-checkout banner — explains why only the plan picker shows */}
            {isResumeCheckout && (
              <div style={{
                padding: '12px 16px',
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: 8,
                color: '#166534',
                fontSize: 14,
                marginBottom: 16
              }}>
                Welcome back{resumeOrgName ? ` — your organization "${resumeOrgName}" is ready` : ''}. Choose a plan below to finish setting up and unlock your dashboard.
              </div>
            )}
            
            {/* Referral Code Input (hidden for Founders) */}
            {!isFoundersInvite && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151',
                  marginBottom: 6
                }}>
                  Referral Code (optional)
                </label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="Enter referral code"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                    textTransform: 'uppercase',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2d6a4f'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                {validatingReferral && (
                  <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Validating...</p>
                )}
                {!validatingReferral && referralValidation.message && (
                  <p style={{ 
                    fontSize: 12, 
                    color: referralValidation.valid ? '#22c55e' : '#ef4444',
                    marginTop: 4
                  }}>
                    {referralValidation.message}
                  </p>
                )}
              </div>
            )}
            
            {/* Plan Selection */}
            {isFoundersInvite ? (
              /* Founders-only invitation view - PRESERVED unchanged */
              <div style={{ marginBottom: 24 }}>
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  border: '2px solid #f59e0b',
                  borderRadius: 16,
                  padding: 24,
                  textAlign: 'center',
                  marginBottom: 16
                }}>
                  <div style={{ 
                    fontSize: 12, 
                    fontWeight: 700, 
                    color: '#92400e', 
                    marginBottom: 8, 
                    textTransform: 'uppercase', 
                    letterSpacing: 1 
                  }}>
                    🎉 Exclusive Founders Invitation
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#1f2937', marginBottom: 4 }}>
                    Founders Plan
                  </div>
                  <div style={{ fontSize: 16, color: '#6b7280', marginBottom: 16 }}>
                    Unlimited players forever
                  </div>
                  <div style={{ fontSize: 40, fontWeight: 800, color: '#2d6a4f' }}>
                    $49.99
                    <span style={{ fontSize: 16, fontWeight: 400, color: '#6b7280' }}>/year</span>
                  </div>
                  <div style={{ fontSize: 14, color: '#166534', marginTop: 8, fontWeight: 600 }}>
                    ~$4.17/month • Rate locked for life
                  </div>
                </div>
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 13,
                  color: '#166534',
                  textAlign: 'center'
                }}>
                  ✨ You've been invited to join under the special Founders rate.
                </div>
              </div>
            ) : (
              /* Normal plan selection — 5 tiers including Free */
              <div style={{
                opacity: 1,
                transition: 'opacity 0.3s'
              }}>
                <div style={{ 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151',
                  marginBottom: 12
                }}>
                  Select your plan:
                </div>
                
                {/* Billing Cycle Toggle — always shown so users can switch
                    monthly/annual before picking a paid plan. Harmless when Free
                    is selected (Free is $0 either way). */}
                {(
                  <div style={{
                    display: 'flex',
                    background: '#f3f4f6',
                    borderRadius: 8,
                    padding: 4,
                    marginBottom: 16
                  }}>
                    <button
                      type="button"
                      onClick={() => setBillingCycle('monthly')}
                      style={{
                        flex: 1,
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: billingCycle === 'monthly' ? '#fff' : 'transparent',
                        color: billingCycle === 'monthly' ? '#1f2937' : '#6b7280',
                        boxShadow: billingCycle === 'monthly' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        transition: 'all 0.2s'
                      }}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingCycle('annual')}
                      style={{
                        flex: 1,
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: billingCycle === 'annual' ? '#fff' : 'transparent',
                        color: billingCycle === 'annual' ? '#1f2937' : '#6b7280',
                        boxShadow: billingCycle === 'annual' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        transition: 'all 0.2s'
                      }}
                    >
                      Annual <span style={{ color: '#22c55e', fontSize: 12 }}>(Save ~20%)</span>
                    </button>
                  </div>
                )}
                
                {/* Tier List — compact one-row-per-plan layout (name · players ·
                    price · radio). Far shorter than a card grid so the whole
                    step fits on one screen without scrolling. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {SUBSCRIPTION_TIERS.map((tier) => {
                    const isSelected = selectedTier === tier.id;
                    const priceText = tier.isFree
                      ? 'Free'
                      : `$${billingCycle === 'annual' ? tier.annualPrice.toFixed(2) : tier.monthlyPrice.toFixed(2)}${billingCycle === 'annual' ? '/yr' : '/mo'}`;
                    return (
                      <div
                        key={tier.id}
                        onClick={() => setSelectedTier(tier.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          border: isSelected ? '2px solid #2d6a4f' : '2px solid #e5e7eb',
                          borderRadius: 8,
                          padding: '8px 12px',
                          cursor: 'pointer',
                          background: isSelected ? '#f0fdf4' : '#fff',
                          transition: 'all 0.2s'
                        }}
                      >
                        {/* Radio indicator */}
                        <div style={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          border: isSelected ? '5px solid #2d6a4f' : '2px solid #cbd5e1',
                          flexShrink: 0,
                          transition: 'all 0.2s'
                        }} />
                        {/* Name + players */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>
                            {tier.name}
                          </span>
                          {tier.popular && (
                            <span style={{
                              marginLeft: 6,
                              background: '#f59e0b',
                              color: '#fff',
                              padding: '1px 6px',
                              borderRadius: 4,
                              fontSize: 9,
                              fontWeight: 700,
                              verticalAlign: 'middle'
                            }}>
                              POPULAR
                            </span>
                          )}
                          <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
                            {tier.players} regular players
                          </span>
                          {tier.games && (
                            <span title="Games (skins, Nassau, Sixes, Stableford and more) come with this plan" style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#166534', background: '#dcfce7', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                              🎮 Games
                            </span>
                          )}
                        </div>
                        {/* Price */}
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <span style={{ fontSize: 15, fontWeight: 800, color: '#2d6a4f' }}>
                            {priceText}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Error Message */}
            {error && (
              <div style={{
                padding: '12px 16px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                color: '#b91c1c',
                fontSize: 14,
                marginBottom: 16,
                marginTop: 16
              }}>
                {error}
              </div>
            )}
            
            {/* Summary & Submit — tier-aware copy (no 30-day-trial language) */}
            <div style={{
              background: '#f9fafb',
              borderRadius: 12,
              padding: 20,
              marginBottom: 16,
              marginTop: 20
            }}>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>Summary</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
                    {organizationName}
                  </div>
                  <div style={{ fontSize: 14, color: '#6b7280' }}>
                    {selectedTierData?.name} Plan{isFreeTier ? '' : (selectedTier === 'founders' ? ' (Annual)' : ` (${billingCycle})`)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {isFreeTier ? (
                    <>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>$0</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>Forever free</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>
                        ${displayPrice?.toFixed(2)}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {selectedTier === 'founders' || billingCycle === 'annual' ? 'per year' : 'per month'}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {isFreeTier && (
                <div style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  background: '#f0fdf4',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#166534'
                }}>
                  Up to 8 regular players · occasional guests free · Upgrade any time
                </div>
              )}
              {referralValidation.valid && !isFoundersInvite && !isFreeTier && (
                <div style={{ 
                  marginTop: 12, 
                  padding: '8px 12px', 
                  background: '#dcfce7', 
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#166534'
                }}>
                  ✨ Referral credit will be applied after first payment
                </div>
              )}
            </div>
            
            {/* Submit Button — label depends on tier */}
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '16px',
                background: isSubmitting ? '#9ca3af' : '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 700,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(34, 197, 94, 0.3)'
              }}
              onMouseEnter={(e) => { if (!isSubmitting) e.target.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { if (!isSubmitting) e.target.style.opacity = '1'; }}
            >
              {isSubmitting 
                ? 'Creating Account...' 
                : (isFreeTier ? '🎉 Create Free Account' : '🚀 Continue to Checkout')}
            </button>
            
            <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 12 }}>
              {isFreeTier
                ? 'No credit card required.'
                : 'You\'ll enter your payment info on the next screen.'}
            </p>
          </form>
        )}
        
        {/* Login Link */}
        <div style={{
          marginTop: 20,
          padding: '14px',
          background: '#f9fafb',
          borderRadius: 8,
          textAlign: 'center'
        }}>
          <span style={{ fontSize: 14, color: '#6b7280' }}>
            Already have an account?{' '}
          </span>
          <Link 
            to="/login"
            style={{ fontSize: 14, color: '#2d6a4f', textDecoration: 'none', fontWeight: 600 }}
          >
            Sign in
          </Link>
        </div>
      </div>
      
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default RegisterPage;
